// Imported with `?img` (asset/resource -> URL string, see webpack.common.js) rather than the
// default SVGR-component form, and rendered through a plain <img>. These are hand-exported
// design assets with inconsistent/missing viewBox metadata, so an inline <svg> component
// scales unpredictably (content can overflow its box); <img> always scales its content to the
// element's box regardless of the source file's internal dimensions, matching how the
// API-provided launcher_icon already renders.
import DiscoverLive from './DiscoverLive.svg?img';
import EnrichFirst from './EnrichFirst.svg?img';
import FaithFirst from './FaithFirst.svg?img';
import ForYou from './ForYou.svg?img';
import MemoryLane from './MemoryLane.svg?img';
import MusicFirst from './MusicFirst.svg?img';
import SeniorTechConnect from './SeniorTechConnect.svg?img';
import StageAccess from './StageAccess.svg?img';
import Zinnia from './Zinnia.svg?img';
import Spiro100 from './spiro100.svg?img';

// One fixed icon per channel - no OS/browser theme awareness, always shown regardless of
// light/dark mode. Keyed by every known API name/label spelling for that channel (normalized
// via getChannelIconKey below).
//
// The 'coro-health-*'/'*-tv' aliases are the actual app-registry name slugs already confirmed
// in ListContentActivity's CHANNEL_LOGO_MAP (src/Components/ContentActivity/
// ListContentActivity.component.jsx) - reused here so this map matches real API values, not
// just the local filenames.
const CHANNEL_ICON_ENTRIES = [
  { keys: ['discoverlive'], icon: DiscoverLive },
  { keys: ['enrichfirst', 'corohealthenrichfirst'], icon: EnrichFirst },
  { keys: ['faithfirst', 'corohealthfaithfirst'], icon: FaithFirst },
  { keys: ['foryou'], icon: ForYou },
  { keys: ['memorylane', 'memorylanetv'], icon: MemoryLane },
  { keys: ['musicfirst', 'corohealthmusicfirst'], icon: MusicFirst },
  { keys: ['seniortechconnect'], icon: SeniorTechConnect },
  { keys: ['stageaccess'], icon: StageAccess },
  { keys: ['zinnia', 'zinniatv'], icon: Zinnia },
  {
    // The DB/API name for this channel is suffixed per environment (e.g. "spiro100_eb"), so
    // this entry also matches by prefix below rather than only an exact "spiro100" name.
    keys: ['spiro100'],
    prefixMatch: 'spiro100',
    icon: Spiro100,
  },
];

// Lowercase, strip everything but letters/digits, so "Faith First", "FaithFirst" and
// "coro-health-faith-first" all normalize to the same key regardless of spacing/casing/dashes.
export const getChannelIconKey = (name) =>
  String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const CHANNEL_ICON_MAP = CHANNEL_ICON_ENTRIES.reduce((map, entry) => {
  entry.keys.forEach((key) => {
    map[key] = entry;
  });
  return map;
}, {});

// Tries each candidate name/label (in order) and returns the first matching entry's icon URL,
// or null if none match. Callers typically pass both metadata.name and metadata.launcher_label
// since either may hold the channel's display name.
//
// Exact normalized-key matches are checked first (across all candidates), then entries opting
// into prefixMatch are checked (e.g. "spiro100_eb" matches the "spiro100" entry) - kept as a
// second pass, and opt-in per entry, so an unrelated channel name that happens to start with
// another channel's key can't accidentally steal its icon.
export const getChannelIconUrl = (...candidateNames) => {
  for (const name of candidateNames) {
    const key = getChannelIconKey(name);
    if (key && CHANNEL_ICON_MAP[key]) {
      return CHANNEL_ICON_MAP[key].icon;
    }
  }
  for (const name of candidateNames) {
    const key = getChannelIconKey(name);
    if (!key) continue;
    const prefixEntry = CHANNEL_ICON_ENTRIES.find(
      (entry) => entry.prefixMatch && key.startsWith(entry.prefixMatch)
    );
    if (prefixEntry) return prefixEntry.icon;
  }
  return null;
};

export default CHANNEL_ICON_MAP;
